/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid,jsx-a11y/role-supports-aria-props */
import React, { useEffect, useState, useRef, Fragment } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Snackbar } from "@material-ui/core";
import { Spinner } from "react-bootstrap";

import axios from "axios";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../_metronic/_partials/controls";
import { EventEditForm } from "./Components/EventEditForm";
import { EventQuestionsEditor } from "./Components/EventQuestionsEditor";
import { EventScheduleEditor } from "./Components/EventScheduleEditor";
import { useEventsSuperAdmin } from "./Components/useEventsSuperAdmin";
import { useSubheader } from "../../../_metronic/layout";
import { SnackbarWrapper } from "../Snackbar/SnackbarWrapper";
import moment from "moment";
import { checkAndReplace } from "../../helpers/Formatter";

const DEFAULT_IMAGES = [
  {
    label: "Default Robotics Image",
    value: "https://websites-events.s3.us-east-2.amazonaws.com/IMG-20210929-WA0003.jpg"
  },
  {
    label: "Default Coding Image",
    value: "https://websites-events.s3.us-east-2.amazonaws.com/hourOfCode.png"
  }
];

const initEvent = {
  branch_id: 0,
  active_id: "1",
  fee: 0,
  // Blank: staff paste the event's own image link, or pick one of
  // DEFAULT_IMAGES from the field's dropdown.
  image: null,
  description: "",
  max_students: 0,
  grade_min_nbr: 1,
  grade_max_nbr: 12,
  startdate: "",
  enddate: "",
  // Optional registration window; blank = open now / until the event ends.
  registration_start_date: "",
  registration_end_date: "",
  workshop_name: "",
  form_link: "",
  full_ind: "0",
  // New events are aimed at students unless staff choose Parent/Tutor.
  student_only_ind: "1",
  // The website asks the registering parent for an emergency contact unless
  // staff choose No.
  emergency_contact_ind: "1",
  // Taxable event (V094). A new event's default comes from its branch's
  // country (taxDefaultForCountry), set once the branch details load.
  fee_tax_ind: 0,
  // Workshop (event_type 1, V097): no registration questions.
  event_type_id: 1,
  start_time: "",
  end_time: "",
  price_subheader: ""
};

// Owner's rule: events at Canadian branches are taxable by default; US and
// Jamaica (Kingston) branches are not. Staff can change it per event.
// The country table stores names in capitals ("CANADA"), so compare without
// case.
const taxDefaultForCountry = (countryName) =>
  String(countryName || "").trim().toUpperCase() === "CANADA" ? 1 : 0;

export function EventEdit({
  history,
  match: {
    params: { id, duplicate, dest_id },
  },
}) {
  const branch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );
  // Subheader
  const suhbeader = useSubheader();

  //batch data
  const [data, setData] = useState();

  // Tabs
  const [tab, setTab] = useState("basic");
  const [title, setTitle] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [editSuccessOpen, setEditSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [editErrorOpen, setEditErrorOpen] = useState(false);
  // The server's own message for a refused save (e.g. a 409 on changing the
  // type while active registration questions exist); generic otherwise.
  const [errorMessage, setErrorMessage] = useState("");
  // GET /api/workshops/event-types: [{ event_type_id, event_type_name,
  // custom_fields_ind, schedule_ind, priced_options_ind, order_num }]; null
  // until loaded. Tabs follow the flags, never the type's name.
  const [eventTypes, setEventTypes] = useState(null);
  // The Event Type chosen on Basic info, saved or not (EventEditForm reports
  // it), and what the questions tab loaded: { status, questionsAllowed,
  // hasActiveSaved } (EventQuestionsEditor reports it). Together they decide
  // whether the Registration questions tab is shown and usable.
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [questionsSummary, setQuestionsSummary] = useState(null);
  // What the Schedule tab loaded: { status, hasSavedSessions, dirty }
  // (EventScheduleEditor reports it).
  const [scheduleSummary, setScheduleSummary] = useState(null);
  const [eventsToAdd, setEventsToAdd] = useState([]);
  const [values, setValues] = useState();
  // New event only: { branch_id, fee_tax_ind } — the Taxable default from the
  // branch's country, tagged with the branch it was looked up for — or null
  // until the first lookup answers (the form waits so the box is right when
  // shown). A branch switch gives a new object, so the form is rebuilt for
  // the new branch whatever its country.
  const [newEventDefaults, setNewEventDefaults] = useState(null);
  const btnRef = useRef();
  // Registration questions: Super Admins only (a temporary UI gate, see
  // useEventsSuperAdmin). Event Type and Schedule are for all event staff.
  const isSuperAdmin = useEventsSuperAdmin();

  initEvent.branch_id = branch.branch_id;

  useEffect(() => {
    const getEventInfo = async () => {
      const event_info_url = `api/workshops/edit/${id}`;
      const res = await axios(event_info_url);
      let event = res.data.results[0]
      setData({
        ...event,
        // Optional fields that can be NULL in the DB; inputs and the string
        // schema need "" not null.
        grade_min_nbr: event.grade_min_nbr ?? "",
        grade_max_nbr: event.grade_max_nbr ?? "",
        price_subheader: event.price_subheader ?? "",
        form_link: event.form_link ?? "",
        // Edit and Duplicate keep the event's own Taxable setting.
        fee_tax_ind: Number(event.fee_tax_ind) === 1 ? 1 : 0,
        // ... and its type (a server without V097 sends none: Workshop).
        event_type_id: event.event_type_id ?? 1,
        // A server without the field collects the contact, as before it.
        emergency_contact_ind: String(event.emergency_contact_ind ?? 1) === "0" ? 0 : 1,
        // A copy is a new run of the event: the source's registration window
        // has usually passed and would show the copy as Full on the website
        // the moment it is saved.
        ...(duplicate
          ? { registration_start_date: "", registration_end_date: "" }
          : {}),
      });

    };
    id && getEventInfo();
  }, [id, duplicate]);

  // Sent explicitly, as EventList/EventRow do: after a hard refresh
  // axios.defaults has no x-auth-token until the rehydrate saga resolves.
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);
  useEffect(() => {
    if (!authToken) return undefined;
    let cancelled = false;
    axios("api/workshops/event-types", { headers: { "x-auth-token": authToken } })
      .then((res) => {
        if (cancelled) return;
        setEventTypes(
          [...(res.data.results || [])].sort((a, b) => Number(a.order_num) - Number(b.order_num))
        );
      })
      .catch((e) => {
        console.error("event types: ", e);
        if (cancelled) return;
        setEventTypes([]);
        // Saving still works: the event keeps its stored type (a new event
        // Workshop); only the picker is empty.
        setErrorMessage(e.response?.data?.message || "Couldn't load event types");
        setErrorOpen(true);
      });
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  useEffect(() => {
    if (id || !branch?.branch_id) return undefined;
    const branchId = branch.branch_id;
    let cancelled = false;
    // Capped at 5 s: a slow or hanging lookup falls back to unticked (the
    // catch below) instead of holding the form back.
    axios(`api/branches/details/${branchId}`, { timeout: 5000 })
      .then((res) => {
        if (cancelled) return;
        setNewEventDefaults({
          branch_id: branchId,
          fee_tax_ind: taxDefaultForCountry(res.data?.results?.[0]?.country_name),
        });
      })
      .catch((e) => {
        console.error("branch details: ", e);
        // Unknown country: leave the box unticked; staff can tick it.
        if (!cancelled) setNewEventDefaults({ branch_id: branchId, fee_tax_ind: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [id, branch?.branch_id]);

  useEffect(() => {
    if (data && id) {
      setValues(data);
    } else if (!id && newEventDefaults) {
      setValues({ ...initEvent, ...newEventDefaults });
    }
  }, [id, data, newEventDefaults]);

  useEffect(() => {
    let _title = id ? "" : "New Event";
    if (id && duplicate) {
      _title = `Duplicate Event`;
    } else if (id) {
      _title = `Edit Event - ${id}`;
    }

    setTitle(_title);
    suhbeader.setTitle(_title);
  }, [id]);

  //Get list of event in branch
  useEffect(() => {
    const getEvents = async () => {
      try {
        const events_url = `api/workshops/workshops/${branch.branch_id}`;
        const res = await axios(events_url);

        dest_id
          ? setEventsToAdd(
              res.data.results.map((e) => {
                return e.workshop_id;
              })
            )
          : setEventsToAdd(eventsToAdd);
      } catch (e) {
        console.log("events in branch: ", e);
      }
    };
    id && getEvents();
  }, [id, dest_id]);

  // The type is the form's: a new event starts as a Workshop (initEvent), an
  // edit or a copy as the loaded event's type.
  const saveEvent = async (values) => {
    try {
      await submitEvent({
        ...values,
        event_type_id: Number(values.event_type_id) || 1,
        emergency_contact_ind: String(values.emergency_contact_ind) === "0" ? 0 : 1,
      });
    } catch (e) {
      console.error("save event: ", e);
      const message = e.response?.data?.message || "";
      // The type change is refused while active questions, sessions or
      // option prices exist; say where to deal with them (the questions tab
      // is a Super Admin's).
      setErrorMessage(
        e.response?.status === 409 && /registration questions/i.test(message)
          ? isSuperAdmin
            ? "Deactivate the questions on the Registration questions tab first"
            : "This event has registration questions: ask a Super Admin to deactivate them first"
          : e.response?.status === 409 && /sessions before changing its type/i.test(message)
          ? "Remove the sessions on the Schedule tab first"
          : e.response?.status === 409 && /option prices/i.test(message)
          ? isSuperAdmin
            ? "Set the option prices to 0 on the Registration questions tab first"
            : "This event has priced options: ask a Super Admin to set them to 0 first"
          : message
      );
      id ? setEditErrorOpen(true) : setErrorOpen(true);
    }
  };

  const submitEvent = async (values) => {
    if (!id) {
      //save new event post
      const eventsUrl = "api/workshops";
      // Always the branch selected now, never one the form was built for
      // before a branch switch.
      values.branch_id = branch.branch_id;
      values.description = checkAndReplace(values.description)
      values.workshop_name = checkAndReplace(values.workshop_name)
      values.price_subheader = checkAndReplace(values.price_subheader)
      
      const result = await axios.post(eventsUrl, values);
      if (result.data !== "failed") {
        setSuccessOpen(true);
        setTimeout(() => {
          history.push(`/events/event-list`);
        }, 1000);
      } else {
        setErrorOpen(true);
      }
    } else if (id && duplicate && !dest_id) {
      //save new duplicated event post
      const eventsUrl = "api/workshops";
      
      const result = await axios.post(eventsUrl, values);
      if (result.data !== "failed") {
        setSuccessOpen(true);
        setTimeout(() => {
          history.push(`/events/event-list`);
        }, 1000);
      } else {
        setErrorOpen(true);
      }
    } else if (id && duplicate && dest_id) {
      values.branch_id = dest_id;
      
      //edit duplicate event patch
      const editEventUrl = `api/workshops/edit/`;
      const result = await axios.patch(editEventUrl + id, values);
      
      if (result.data === "success") {
        setEditSuccessOpen(true);
        setTimeout(() => {
          history.push("/events/event-list");
        }, 1000);
      } else {
        setEditErrorOpen(true);
      }
    } else {
      
      //edit event patch
      const editEventUrl = `api/workshops/edit/`;
      const result = await axios.patch(editEventUrl + id, values);
      
      if (result.data === "success") {
        setEditSuccessOpen(true);
        setTimeout(() => {
          history.push("/events/event-list");
        }, 1000);
      } else {
        setEditErrorOpen(true);
      }
    }
  };

  const handleClose = () => {
    setSuccessOpen(false);
    setErrorOpen(false);
    setEditErrorOpen(false);
    setErrorMessage("");
  };

  // The header Save (Basic info) and Back both leave the page, which would
  // drop unsaved edits on the questions or schedule tab, open or hidden: ask
  // first, naming what would be lost.
  const leaveQuestionsOk = () => {
    const unsaved = [
      questionsSummary?.dirty && "registration question",
      scheduleSummary?.dirty && "schedule",
    ].filter(Boolean);
    return (
      unsaved.length === 0 ||
      window.confirm(`You have unsaved ${unsaved.join(" and ")} changes. Leave anyway?`)
    );
  };

  // Basic info's validation errors are off screen when Save is pressed from
  // the questions tab, so name the first one in the error toast.
  const handleInvalidBasicInfo = (errors) => {
    if (tab === "basic") return;
    const first = (function firstMessage(e) {
      if (typeof e === "string") return e;
      if (e && typeof e === "object") {
        for (const v of Object.values(e)) {
          const m = firstMessage(v);
          if (m) return m;
        }
      }
      return null;
    })(errors);
    setErrorMessage(`Basic info has a problem: ${first || "check the fields"}`);
    setEditErrorOpen(true);
  };

  const saveEventClick = () => {
    if (!leaveQuestionsOk()) return;
    if (btnRef && btnRef.current) {
      btnRef.current.click();
    } else {
      saveEvent(null);
    }
  };

  // Registration questions tab: only for a Super Admin (see
  // useEventsSuperAdmin), on a saved event being edited (not New or
  // Duplicate), once the event types and its questions have loaded.
  //  "none"     the chosen type does not allow questions and none are active;
  //  "disabled" the chosen type allows questions but the saved one does
  //             not: the event must be saved with the new type first;
  //  "enabled"  otherwise, including the read-only cases (the chosen or the
  //             saved type has no questions but active ones remain to
  //             deactivate), and a failed load (so its Try again is reachable).
  const savedWorkshopId = id && !duplicate ? id : null;
  const questionsWorkshopId = isSuperAdmin ? savedWorkshopId : null;
  const selectedType = (eventTypes || []).find(
    (t) => String(t.event_type_id) === String(selectedTypeId)
  );
  let questionsTab = "none";
  if (questionsWorkshopId && eventTypes && questionsSummary) {
    if (questionsSummary.status === "error") {
      questionsTab = "enabled";
    } else if (questionsSummary.status === "loaded") {
      const selectedAllows = selectedType
        ? Number(selectedType.custom_fields_ind) === 1
        : questionsSummary.questionsAllowed;
      if (selectedAllows || questionsSummary.hasActiveSaved) {
        // Disabled only when the new choice allows questions but the saved
        // type does not (the server goes by the saved type). The reverse
        // stays enabled, read-only, so staff can deactivate the questions
        // the type change needs gone.
        questionsTab =
          selectedAllows && !questionsSummary.questionsAllowed ? "disabled" : "enabled";
      }
    }
  }
  // Never hide the open tab (e.g. after deactivating the last question under
  // a type without questions); it says why it is empty.
  if (tab === "questions" && questionsTab === "none") questionsTab = "enabled";

  // Schedule tab, for everyone on a saved event being edited, by the same
  // rules on schedule_ind: shown when the chosen type has a schedule or the
  // event already has sessions (then read-only, to remove them), disabled
  // while only the unsaved choice has one.
  const savedType = (eventTypes || []).find(
    (t) => String(t.event_type_id) === String(data?.event_type_id)
  );
  let scheduleTab = "none";
  if (savedWorkshopId && eventTypes && data && scheduleSummary) {
    if (scheduleSummary.status === "error") {
      scheduleTab = "enabled";
    } else if (scheduleSummary.status === "loaded") {
      const savedAllows = Number(savedType?.schedule_ind) === 1;
      const selectedAllows = selectedType
        ? Number(selectedType.schedule_ind) === 1
        : savedAllows;
      if (selectedAllows || scheduleSummary.hasSavedSessions) {
        scheduleTab = selectedAllows && !savedAllows ? "disabled" : "enabled";
      }
    }
  }
  if (tab === "schedule" && scheduleTab === "none") scheduleTab = "enabled";

  const backToEventList = () => {
    if (!leaveQuestionsOk()) return;
    history.push(`/events`);
  };

  return (
    <Fragment>
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={
          successOpen
            ? successOpen
            : editSuccessOpen
            ? editSuccessOpen
            : errorOpen
            ? errorOpen
            : editErrorOpen
        }
        // A refusal's explanation stays until closed, and a click elsewhere
        // on the page does not close it.
        autoHideDuration={errorMessage ? null : 3000}
        onClose={(e, reason) => reason !== "clickaway" && handleClose()}
      >
        <SnackbarWrapper
          onClose={handleClose}
          variant={successOpen || editSuccessOpen ? "success" : "error"}
          message={
            successOpen
              ? "Added the event successfully"
              : editSuccessOpen
              ? "Edited the event successfully"
              : errorMessage || "Something went wrong"
          }
        />
      </Snackbar>
      <Card>
        {/* {actionsLoading && <ModalProgressBar />} */}
        <CardHeader title={title}>
          <CardHeaderToolbar>
            <button
              type="button"
              onClick={backToEventList}
              className="btn btn-light"
            >
              <i className="fa fa-arrow-left"></i>
              Back
            </button>
            {`  `}
            <button
              type="submit"
              className="btn btn-primary ml-2"
              onClick={saveEventClick}
            >
              Save
            </button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <ul className="nav nav-tabs nav-tabs-line " role="tablist">
            <li className="nav-item" onClick={() => setTab("basic")}>
              <a
                className={`nav-link ${tab === "basic" && "active"}`}
                data-toggle="tab"
                role="tab"
                aria-selected={(tab === "basic").toString()}
              >
                Basic info
              </a>
            </li>
            {questionsTab !== "none" && (
              <li
                className="nav-item"
                onClick={questionsTab === "enabled" ? () => setTab("questions") : undefined}
                title={
                  questionsTab === "disabled"
                    ? "Save the event with the new type first"
                    : undefined
                }
              >
                <a
                  className={`nav-link ${tab === "questions" && "active"} ${
                    questionsTab === "disabled" ? "disabled" : ""
                  }`}
                  data-toggle="tab"
                  role="tab"
                  aria-selected={(tab === "questions").toString()}
                  aria-disabled={questionsTab === "disabled"}
                >
                  Registration questions
                </a>
              </li>
            )}
            {scheduleTab !== "none" && (
              <li
                className="nav-item"
                onClick={scheduleTab === "enabled" ? () => setTab("schedule") : undefined}
                title={
                  scheduleTab === "disabled"
                    ? "Save the event with the new type first"
                    : undefined
                }
              >
                <a
                  className={`nav-link ${tab === "schedule" && "active"} ${
                    scheduleTab === "disabled" ? "disabled" : ""
                  }`}
                  data-toggle="tab"
                  role="tab"
                  aria-selected={(tab === "schedule").toString()}
                  aria-disabled={scheduleTab === "disabled"}
                >
                  Schedule
                </a>
              </li>
            )}
          </ul>
          <div className="mt-5">
            {/* Every tab is hidden rather than unmounted, as in BatchEdit:
                unmounting Basic info would drop its unsaved fields and null
                btnRef (the header Save), and unmounting the questions would
                drop their unsaved edits and the summary the tab rules use
                (the schedule likewise). */}
            {values && (
              <div style={{ display: tab === "basic" ? undefined : "none" }}>
                <EventEditForm
                  event={values}
                  btnRef={btnRef}
                  saveEvent={saveEvent}
                  defaultImages={DEFAULT_IMAGES}
                  eventTypes={eventTypes}
                  showEventType
                  onTypeChange={setSelectedTypeId}
                  onInvalidSubmit={handleInvalidBasicInfo}
                />
              </div>
            )}
            {questionsWorkshopId && (
              <div style={{ display: tab === "questions" ? undefined : "none" }}>
                <EventQuestionsEditor
                  workshopId={questionsWorkshopId}
                  selectedType={selectedType}
                  savedType={savedType}
                  eventFee={data ? data.fee : undefined}
                  onSummary={setQuestionsSummary}
                />
              </div>
            )}
            {savedWorkshopId && data && (
              <div style={{ display: tab === "schedule" ? undefined : "none" }}>
                <EventScheduleEditor
                  workshopId={savedWorkshopId}
                  event={data}
                  selectedType={selectedType}
                  savedType={savedType}
                  onSummary={setScheduleSummary}
                />
              </div>
            )}
            {/* Until the event (Edit/Duplicate) or the branch's Taxable
                default (New Event) has loaded, say so rather than show an
                empty tab. */}
            {tab === "basic" && !values && (
              <div className="d-flex align-items-center text-muted py-5">
                <Spinner animation="border" size="sm" className="mr-2" />
                Loading event form…
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </Fragment>
  );
}
